/**
 * Runtime perf kill-switches for CPU instrumentation.
 *
 * Each flag selects between the current (default) behavior of a subsystem
 * and a cheaper probe variant, so a single URL-param flip yields a clean
 * differential CPU measurement. Flags NEVER change behavior unless
 * explicitly set — the default URL must measure identically to production.
 *
 * Spec syntax (page URL): `?perf=sabCrc:off,dispatcher:backoff`
 *
 * Transport into workers: worker script URLs are emitted by the bundler and
 * cannot carry page query params, so spawn sites pass the spec through the
 * `Worker` `name` option via {@link perfWorkerName} ('<base>#perf=<spec>',
 * read back here from `self.name`). Workers that spawn child workers
 * propagate the same way. AudioWorklets have neither `name` nor a usable
 * `location` — no current flag is consumed inside a worklet.
 *
 * Known flags (consumers document their own values):
 *   speech      'on' (default) | 'off'      — SpeechDetector 50ms poll
 *   render      'on' (default) | 'off'      — canvas renderer drawImage work
 *   jitterPoll  '100' (default) | '500' | 'off' — AppVideoDecoder buffered-frame
 *               poll interval (ms)
 *   renderDelay '0' (default) | '<ms>'      — test probe: busy-wait inside every
 *               render (a render slower than the 33 ms tick)
 *   renderStats 'off' (default) | 'on'      — log the renderer's tick/render
 *               counters every 5 s even while it keeps up
 *   renderer    'auto' (default) | '2d' | 'webgpu' — the composed renderer's
 *               painter backend ('auto' is the platform default; read in
 *               the video worker)
 *   directOutput 'auto' (default) | 'off'   — a single shown video goes to a
 *               direct <video> element on WebKit; 'off' keeps the canvas
 *   thumbnailFps '15' (default) | '<n>'     — how often the film-strip
 *               thumbnails are repainted at most (each paint is a canvas
 *               commit; on WebKit a CPU cost in the GPU process)
 *   hevcProbe   'auto' (default) | 'on' | 'off' | 'unavailable' — the video
 *               worker's HEVC hardware-encoder throughput probe: 'auto' runs
 *               it on desktop WebKit only, 'on' on any WebKit (phones too),
 *               'off' skips it and keeps HEVC, 'unavailable' forces the
 *               H.264 fallback on any platform
 *
 * Mirror of `packages/tv-engine/src/perfFlags.ts` (this package cannot be
 * imported from tv-engine and the engine workers cannot import src/). Keep
 * the two in sync.
 */
const PERF_NAME_MARKER = '#perf=';
let cachedSpec = null;
function readSpec() {
    try {
        const g = globalThis;
        if (typeof g.window !== 'undefined' && g.window === globalThis) {
            return (new URLSearchParams(g.location?.search ?? '').get('perf') ?? '');
        }
        const name = typeof g.name === 'string' ? g.name : '';
        const idx = name.indexOf(PERF_NAME_MARKER);
        return idx >= 0 ? name.slice(idx + PERF_NAME_MARKER.length) : '';
    }
    catch {
        return '';
    }
}
/** Raw flag spec ('' when no flags are set). */
export function getPerfSpec() {
    if (cachedSpec === null) {
        cachedSpec = readSpec();
    }
    return cachedSpec;
}
/**
 * Value of one flag, or `defaultValue` when unset. A bare flag with no
 * `:value` reads as 'on'.
 */
export function getPerfFlag(flag, defaultValue) {
    if (getPerfSpec() === '')
        return defaultValue;
    for (const entry of getPerfSpec().split(',')) {
        const [k, v] = entry.split(':');
        if (k === flag)
            return v === undefined || v === '' ? 'on' : v;
    }
    return defaultValue;
}
/**
 * Worker `name` carrying the current spec to a child worker:
 * `new Worker(url, { type: 'module', name: perfWorkerName('videoWorker') })`.
 * Also improves thread labeling in devtools/profilers.
 */
export function perfWorkerName(base) {
    const spec = getPerfSpec();
    return spec === '' ? base : `${base}${PERF_NAME_MARKER}${spec}`;
}
